import { z } from "zod";
import httpClient from "@/config/axios";
import { rosterChangeCellSchema } from "./changeNotices";

const baseSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("previous_issued"),
    version: z.number(),
    issued_at: z.string().nullable(),
    is_active_issued: z.boolean(),
  }),
  z.object({
    kind: z.literal("issued"),
    version: z.number(),
    issued_at: z.string().nullable(),
    is_active_issued: z.boolean(),
  }),
  z.object({
    kind: z.literal("source"),
    version: z.number(),
    schedule_id: z.string().optional(),
  }),
  z.object({
    kind: z.literal("source_missing"),
    version: z.number().nullable(),
  }),
]);
export const versionChangesSchema = z.object({
  schedule: z.object({
    schedule_id: z.string(),
    version: z.number(),
    name: z.string(),
    status: z.string(),
  }),
  group_id: z.string(),
  year: z.number(),
  month: z.number(),
  base: baseSchema.nullable(),
  summary: z.object({
    changed_cells: z.number(),
    affected_nurses: z.number(),
    added_nurses: z.number(),
    removed_nurses: z.number(),
    total_cells: z.number(),
  }),
  cells: z.array(rosterChangeCellSchema),
});
export type VersionChanges = z.infer<typeof versionChangesSchema>;
export const versionChangesKey = (groupId: string, scheduleId: string) =>
  ["roster-version-changes", groupId, scheduleId] as const;

export async function fetchVersionChanges(
  groupId: string,
  scheduleId: string,
  signal?: AbortSignal
) {
  const data = versionChangesSchema.parse(
    await httpClient.get<unknown>(
      `/roster/schedule/${encodeURIComponent(scheduleId)}/changes`,
      { params: { group_id: groupId }, signal }
    )
  );
  if (data.schedule.schedule_id !== scheduleId || data.group_id !== groupId)
    throw new Error("선택한 병동·버전과 변경 내역이 일치하지 않습니다.");
  return data;
}

const saveAsSchema = z.object({
  new_schedule_id: z.string().min(1),
  new_version: z.number(),
  new_name: z.string(),
  status: z.literal("draft"),
  source_schedule_id: z.string(),
  source_version: z.number(),
  changed_cells: z.number(),
  added_nurses: z.number(),
  removed_nurses: z.number(),
  entries_saved: z.number(),
});
export async function saveRosterAsNewVersion(input: {
  readonly sourceScheduleId: string;
  readonly year: number;
  readonly month: number;
  readonly roster: unknown;
  readonly memo: string;
  readonly name?: string;
  readonly groupId?: string;
}) {
  const result = saveAsSchema.parse(
    await httpClient.post<unknown>(
      "/roster/save-as",
      {
        source_schedule_id: input.sourceScheduleId,
        year: input.year,
        month: input.month,
        roster: input.roster,
        memo: input.memo,
        name: input.name?.trim() || null,
      },
      { params: input.groupId ? { group_id: input.groupId } : undefined }
    )
  );
  if (
    result.source_schedule_id !== input.sourceScheduleId ||
    result.new_schedule_id === input.sourceScheduleId
  ) {
    throw new Error("저장된 수정본의 원본 정보가 일치하지 않습니다.");
  }
  return result;
}

export function versionChangesBaseLabel(
  data: Pick<VersionChanges, "base" | "schedule">
): string {
  const base = data.base;
  if (!base) return "비교 기준이 없습니다";
  const issuedDate =
    "issued_at" in base && base.issued_at
      ? base.issued_at.slice(5, 10).replace("-", "/")
      : null;
  const dateLabel = issuedDate ? `(${issuedDate} 마감)` : "";
  switch (base.kind) {
    case "previous_issued":
      return `기준: 직전 마감본 VER${base.version}${dateLabel}`;
    case "issued":
      return `기준: VER${base.version} 마감본${dateLabel}${base.is_active_issued ? "" : " (마감 철회됨)"}`;
    case "source":
      return `기준: VER${base.version}(원본) → VER${data.schedule.version}(수정본)`;
    case "source_missing":
      return `원본 VER${base.version ?? ""}가 삭제돼 비교할 수 없습니다`;
  }
}
